<template>
  <section class="page">

    <div class="page__tabs">
      <button
        type="button"
        class="tabs__btn"
        :class="{ 'is-active': currentTab === 'list' }"
        @click="currentTab = 'list'"
      >{{ $t('chpo.personnel.tabList') }}</button>
      <button
        type="button"
        class="tabs__btn"
        :class="{ 'is-active': currentTab === 'archive' }"
        @click="currentTab = 'archive'"
      >{{ $t('chpo.personnel.tabArchive') }}</button>
    </div>


    <div class="page__head">
      <h2 class="page__title">{{ pageTitle }}</h2>
      <div class="page__head-actions">
        <ActionButton
          v-if="currentTab === 'list'"
          :color="'#2A8A1B'"
          :text="$t('chpo.personnel.addRow')"
          @click="addRow"
        />
        <div class="page__search">
          <input
            class="page__search-input"
            v-model="q"
            type="text"
            :placeholder="$t('chpo.personnel.searchPh')"
            :aria-label="$t('chpo.personnel.searchPh')"
          />
          <span class="page__search-icon" aria-hidden="true"></span>
        </div>
      </div>
    </div>


    <div class="page__table" v-if="currentTab === 'list'">
      <DataTable
        :columns="localizedColumns"
        :rows="filteredRows"
        :row-key="(r) => r.id"
        :row-height="tableRowHeight"
      >
        <template #cell-num="{ row }">
          <div class="cell-text">{{ row.num }}</div>
        </template>
        <template #cell-rank="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.rank_label || row.rank }}</div>
          <SelectField v-else v-model="row.rank" :options="rankOptions" :placeholder="$t('chpo.personnel.selectPh')" width="100%" />
        </template>
        <template #cell-position="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.position_label || row.position }}</div>
          <SelectField v-else v-model="row.position" :options="positionOptions" :placeholder="$t('chpo.personnel.selectPh')" width="100%" />
        </template>
        <template #cell-fio="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.fio }}</div>
          <input v-else class="tbl-input" v-model="row.fio" />
        </template>
        <template #cell-birth="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.birth }}</div>
          <DatePicker v-else v-model="row.birth" />
        </template>
        <template #cell-call="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.call }}</div>
          <DatePicker v-else v-model="row.call" />
        </template>
        <template #cell-arrive="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.arrive }}</div>
          <input v-else class="tbl-input" v-model="row.arrive" />
        </template>
        <template #cell-depart="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.depart }}</div>
          <input v-else class="tbl-input" v-model="row.depart" />
        </template>
        <template #cell-note="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.note }}</div>
          <input v-else class="tbl-input" v-model="row.note" />
        </template>
        <template #cell-order="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.order }}</div>
          <input v-else class="tbl-input" v-model="row.order" />
        </template>


        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div class="actions">
            <button v-if="!row.editing && !row.__new" class="actions__btn actions__btn--card" @click="openDetail(row)" :title="$t('chpo.personnel.caseTitle')" :aria-label="$t('chpo.personnel.case')">
              <span class="actions__label">{{ $t('chpo.personnel.case') }}</span>
            </button>
            <button v-if="!row.editing" class="actions__btn actions__btn--edit" @click="onEdit(row)" :title="$t('chpo.personnel.actionEdit')" :aria-label="$t('chpo.personnel.actionEdit')">
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>

            <template v-else>
              <button class="actions__btn actions__btn--accept" @click="onAccept(row)" :title="$t('chpo.personnel.actionSave')" :aria-label="$t('chpo.personnel.actionSave')">
                <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
              </button>
              <button class="actions__btn actions__btn--decline" @click="onDecline(row)" :title="$t('chpo.personnel.actionCancel')" :aria-label="$t('chpo.personnel.actionCancel')">
                <img class="actions__icon" src="../../assets/images/decline.svg" alt="decline" />
              </button>
            </template>

            <button class="actions__btn actions__btn--archive" @click="onArchive(row)" :title="$t('chpo.personnel.actionArchive')" :aria-label="$t('chpo.personnel.actionArchive')">
              <img class="actions__icon" src="../../assets/images/archive.svg" alt="archive" />
            </button>
          </div>
        </template>
      </DataTable>
    </div>


    <div class="page__table" v-else>
      <DataTable
        :columns="localizedColumns"
        :rows="filteredArchivedRows"
        :row-key="(r) => r.id"
        :row-height="tableRowHeight"
      >
        <template #cell-num="{ row }"><div class="cell-text">{{ row.num }}</div></template>
        <template #cell-rank="{ row }"><div class="cell-text">{{ row.rank_label || row.rank }}</div></template>
        <template #cell-position="{ row }"><div class="cell-text">{{ row.position_label || row.position }}</div></template>
        <template #cell-fio="{ row }"><div class="cell-text">{{ row.fio }}</div></template>
        <template #cell-birth="{ row }"><div class="cell-text">{{ row.birth }}</div></template>
        <template #cell-call="{ row }"><div class="cell-text">{{ row.call }}</div></template>
        <template #cell-arrive="{ row }"><div class="cell-text">{{ row.arrive }}</div></template>
        <template #cell-depart="{ row }"><div class="cell-text">{{ row.depart }}</div></template>
        <template #cell-note="{ row }"><div class="cell-text">{{ row.note }}</div></template>
        <template #cell-order="{ row }"><div class="cell-text">{{ row.order }}</div></template>
        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div class="actions">
            <button class="actions__btn actions__btn--edit" @click="onRestore(row)" :title="$t('chpo.personnel.actionRestore')" :aria-label="$t('chpo.personnel.actionRestore')">
              <img class="actions__icon" src="../../assets/images/unarchive.svg" alt="restore" />
            </button>
          </div>
        </template>
      </DataTable>
    </div>


    <!-- Расширенные поля создания (ТЗ «Рабочие задачи» блок 6.1):
         показываем только для новой строки при её редактировании.
         Для существующих юзеров эти поля правятся через админ-панель. -->
    <div
      v-if="currentTab === 'list' && editingNewRow"
      class="extra-form"
    >
      <div class="extra-form__title">{{ $t('chpo.personnel.extraTitle') }}</div>
      <div class="extra-form__grid">
        <label class="extra-form__field">
          <span>{{ $t('chpo.personnel.iinLabel') }} <em>*</em></span>
          <input
            class="tbl-input"
            v-model="editingNewRow.iin"
            maxlength="12"
            :placeholder="$t('chpo.personnel.iinPh')"
            pattern="\d{12}"
            :class="{ 'is-invalid': editingNewRow.iin && !/^\d{12}$/.test(editingNewRow.iin) }"
          />
        </label>
        <label class="extra-form__field">
          <span>{{ $t('chpo.personnel.passwordLabel') }}</span>
          <input class="tbl-input" type="password" v-model="editingNewRow.password" :placeholder="$t('chpo.personnel.passwordPh')" />
        </label>
        <label class="extra-form__field">
          <span>{{ $t('chpo.personnel.categoryLabel') }}</span>
          <SelectField v-model="editingNewRow.user_category" :options="userCategoryOptions" :placeholder="$t('chpo.personnel.selectPh')" width="100%" />
        </label>
        <label class="extra-form__field">
          <span>{{ $t('chpo.personnel.stpnTypeLabel') }}</span>
          <SelectField v-model="editingNewRow.stpn_type" :options="stpnTypeOptions" :placeholder="$t('chpo.personnel.stpnTypePh')" width="100%" />
        </label>
        <label class="extra-form__field">
          <span>{{ $t('chpo.personnel.addressLabel') }}</span>
          <input class="tbl-input" v-model="editingNewRow.address" :placeholder="$t('chpo.personnel.addressPh')" />
        </label>
        <label class="extra-form__field">
          <span>{{ $t('chpo.personnel.phoneLabel') }}</span>
          <input class="tbl-input" v-model="editingNewRow.phone_number" :placeholder="$t('chpo.personnel.phonePh')" />
        </label>
        <label class="extra-form__checkbox">
          <input type="checkbox" v-model="editingNewRow.is_active" />
          <span>{{ $t('chpo.personnel.isActive') }}</span>
        </label>
        <label class="extra-form__checkbox">
          <input type="checkbox" v-model="editingNewRow.is_stpn" />
          <span>{{ $t('chpo.personnel.isStpn') }}</span>
        </label>
      </div>
      <div class="extra-form__hint">
        {{ $t('chpo.personnel.extraHint') }}
      </div>
    </div>

    <div class="page__footer">
      <ViewNavButtons
        class="page__footer-nav"
        :prev-text="$t('nav.back')"
        :next-text="$t('nav.next')"
        :prev-to="{ path: '/chpo/decision-start' }"
        :next-to="{ path: '/chpo/orders' }"
      />
    </div>

    <PersonnelDetailModal
      :open="detail.open"
      :person-id="detail.personId"
      @close="detail.open = false"
      @saved="onDetailSaved"
    />
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue';
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue';
import ActionButton from '@/components/widgets/ActionButton.vue';
import SelectField from '@/components/widgets/SelectField.vue';
import MaskedInput from '@/components/widgets/MaskedInput.vue';
import DatePicker from '@/components/widgets/DatePicker.vue';
import PersonnelDetailModal from '@/components/modals/PersonnelDetailModal.vue';
import { listChpoUsers, restoreUser, archiveUser as archiveChpoUser, updateChpoUser } from '@/api/chpoUsers';
import { ensureTodayChpoContext, refreshTodayChpoContext } from '@/api/chpo';
import { getPositions, getRanks, createUser, updateUser, linkUserToChpo, deleteUser } from '@/api/personnel';
import { localizedName } from '@/utils/localizedName';

export default {
  name: 'PersonnelListView',
  components: { DataTable, ViewNavButtons, ActionButton, SelectField, MaskedInput, DatePicker, PersonnelDetailModal },
  data() {
    return {
      currentTab: 'list',
      q: '',
      tableRowHeight: 74,
      loading: false,
      error: null,
      columns: [
        { key: 'num',      labelKey: 'chpo.personnel.colNum',      width: 177 },
        { key: 'rank',     labelKey: 'chpo.personnel.colRank',     width: 140 },
        { key: 'position', labelKey: 'chpo.personnel.colPosition', width: 180 },
        { key: 'fio',      labelKey: 'chpo.personnel.colFio',      width: 180 },
        { key: 'birth',    labelKey: 'chpo.personnel.colBirth',    width: 160 },
        { key: 'call',     labelKey: 'chpo.personnel.colCall',     width: 160 },
        { key: 'arrive',   labelKey: 'chpo.personnel.colArrive',   width: 120 },
        { key: 'depart',   labelKey: 'chpo.personnel.colDepart',   width: 120 },
        { key: 'note',     labelKey: 'chpo.personnel.colNote',     width: 120 },
        { key: 'order',    labelKey: 'chpo.personnel.colOrder',    width: 197 },
        { key: 'actions',  label: '',                              width: 210 }
      ],
      rows: [],
      archived: [],
      rankOptions: [],
      positionOptions: [],
      userCategoryOptions: [],
      stpnTypeOptions: [],
      // Модалка «Личное дело» — 5 вкладок, ~110 полей по Figma «Проектирование»
      detail: { open: false, personId: null },
    };
  },
  computed: {
    pageTitle() {
      return this.$t('chpo.personnel.pageTitle')
    },
    localizedColumns() {
      return this.columns.map(c => ({
        ...c,
        label: c.labelKey ? this.$t(c.labelKey) : (c.label || '')
      }))
    },
    filteredRows() {
      const qq = this.q.trim().toLowerCase();
      if (!qq) return this.rows;
      return this.rows.filter(r =>
        [r.num, r.rank, r.position, r.fio, r.birth, r.call, r.arrive, r.depart, r.note, r.order]
          .join(' ')
          .toLowerCase()
          .includes(qq)
      );
    },
    filteredArchivedRows() {
      const qq = this.q.trim().toLowerCase();
      if (!qq) return this.archived;
      return this.archived.filter(r =>
        [r.num, r.rank, r.position, r.fio, r.birth, r.call, r.arrive, r.depart, r.note, r.order]
          .join(' ')
          .toLowerCase()
          .includes(qq)
      );
    },
    // По ТЗ «Рабочие задачи» блок 6.1: если пользователь начал добавлять новую
    // запись, показываем панель дополнительных полей внизу.
    editingNewRow() {
      return this.rows.find(r => r.editing && r.__new) || null
    },
  },
  mounted() {
    this.initLoad();
  },
  methods: {
    async initLoad() {
      this.loading = true; this.error = null;
      try {
        const ctx = await ensureTodayChpoContext();
        this.chpoId = ctx?.id; // Сохраняем ID ЧПО для привязки
        // Важно: сначала загружаем справочники, потом строки
        await this.fetchDictionaries();
        await this.fetchRows();
      } catch (e) {
        this.error = e?.message || this.$t('chpo.personnel.errLoad');
      } finally { this.loading = false; }
    },
    async fetchRows() {
      const activeList = await listChpoUsers({ is_archived: false });
      const archivedList = await listChpoUsers({ is_archived: true });
      
      const getRankLabel = (id) => this.rankOptions.find(o => o.value === id)?.label || '';
      const getPosLabel = (id) => this.positionOptions.find(o => o.value === id)?.label || '';

      const mapUser = (u) => {
        // Если rank/position приходят объектами или ID
        let rankId = (typeof u.rank === 'object' && u.rank) ? u.rank.id : (u.rank_id || u.rank);
        let posId = (typeof u.position === 'object' && u.position) ? u.position.id : (u.position_id || u.position);
        
        // Если rank/position - это строки (названия), пытаемся найти ID в справочниках
        if (typeof u.rank === 'string' && isNaN(u.rank)) {
          const found = this.rankOptions.find(o => o.label === u.rank);
          rankId = found ? found.value : '';
        }
        if (typeof u.position === 'string' && isNaN(u.position)) {
          const found = this.positionOptions.find(o => o.label === u.position);
          posId = found ? found.value : '';
        }
        
        // Пытаемся найти лейбл в справочнике, если нет - берем из объекта или поля label
        const rankLabel = getRankLabel(rankId) || ((typeof u.rank === 'object') ? u.rank.name : (typeof u.rank === 'string' ? u.rank : '')) || '';
        const posLabel = getPosLabel(posId) || ((typeof u.position === 'object') ? u.position.name : (typeof u.position === 'string' ? u.position : '')) || '';

        return {
          id: u.id, // Это chpo_user_id (ID связи)
          user_id: u.user_id || u.id, // ID самого пользователя
          personal_number: u.personal_number ?? '',
          rank: rankId || '',
          rank_label: rankLabel,
          position: posId || '',
          position_label: posLabel,
          fio: u.fio || u.full_name || '',
          birth: this.formatDate(u.birth_date),
          call: this.formatMonthYear(u.service_start_date || u.call_date),
          arrive: u.arrive_from || u.arrival_info || '',
          depart: u.depart_to || u.departure_info || '',
          note: u.note || '',
          order: u.order_ref || u.appointment_info || '',
          editing: false,
        };
      };
      
      this.rows = activeList.map(mapUser).map((r, idx, arr) => ({
        ...r,
        num: r.personal_number !== '' && r.personal_number != null ? String(r.personal_number) : String(idx + 1),
      }));
      this.archived = archivedList.map(mapUser).map((r, idx, arr) => ({
        ...r,
        num: r.personal_number !== '' && r.personal_number != null ? String(r.personal_number) : String(idx + 1),
      }));
    },
    dictName(entity) {
      return localizedName(entity, this.$i18n.locale);
    },
    async fetchDictionaries() {
      try {
        const [ranks, positions] = await Promise.all([getRanks(), getPositions()]);
        this.rankOptions = ranks.map(r => ({ value: r.id, label: this.dictName(r) || r.title || r.name }));
        this.positionOptions = positions.map(p => ({ value: p.id, label: this.dictName(p) || p.title || p.name }));
      } catch (e) {
        console.error('Ошибка загрузки справочников', e);
      }
      // По ТЗ 6.1: подгружаем список категорий и типов СТПН для селектов
      // расширенной формы. Если API недоступен — просто пусто.
      try {
        const { http } = await import('@/api/http');
        const [cats, stpns] = await Promise.all([
          http.get('user-categories/').catch(() => ({ data: [] })),
          http.get('users/stpn-types/').catch(() => http.get('users/stpn/').catch(() => ({ data: [] }))),
        ]);
        const asList = (r) => (Array.isArray(r.data) ? r.data : (r.data?.results || []));
        this.userCategoryOptions = asList(cats).map(c => ({ value: c.id, label: this.dictName(c) || c.name || c.title }));
        this.stpnTypeOptions = asList(stpns).map(s => ({ value: s.id, label: this.dictName(s) || s.name || s.title }));
      } catch (e) {
        console.warn('Категории/СТПН не загружены', e);
      }
    },
    onEdit(row)    {
      row.__backup = { ...row };
      row.editing = true;
    },
    async onAccept(row)  {
      const payload = {
        rank: row.rank, // ID звания
        position: row.position, // ID должности
        full_name: row.fio,
        birth_date: this.parseDate(row.birth),
        service_start_date: this.parseMonthYear(row.call),
        appointment_info: row.order,
        arrival_info: row.arrive || null,
        departure_info: row.depart || null,
        // По ТЗ «Рабочие задачи» блок 6.1: категория теперь из справочника,
        // а не захардкоженное 6. Если не выбрана — оставляем 6 для совместимости.
        user_category: row.user_category || 6,
      };
      // Расширенные поля — только для новой записи, только если заполнены.
      if (row.__new) {
        if (row.iin) payload.iin = String(row.iin).trim();
        if (row.password) payload.password = row.password;
        if (row.stpn_type) payload.stpn_type = row.stpn_type;
        if (row.address) payload.address = row.address;
        if (row.phone_number) payload.phone_number = row.phone_number;
        payload.is_active = row.is_active !== false;
        payload.is_stpn = !!row.is_stpn;
      }

      if (row.__new) {
        const problems = [];
        if (!payload.full_name || !String(payload.full_name).trim()) problems.push(this.$t('chpo.personnel.vFio'));
        if (!payload.rank) problems.push(this.$t('chpo.personnel.vRank'));
        if (!payload.birth_date) problems.push(this.$t('chpo.personnel.vBirth'));
        if (!payload.service_start_date) problems.push(this.$t('chpo.personnel.vCall'));
        // ИИН, если введён — обязан быть ровно 12 цифр (ТЗ 6.1)
        if (payload.iin && !/^\d{12}$/.test(payload.iin)) problems.push(this.$t('chpo.personnel.errIinLength'));
        if (payload.password && String(payload.password).length < 6) problems.push(this.$t('chpo.personnel.errPasswordLen'));
        // Если ввели пароль без ИИН — некому логиниться
        if (payload.password && !payload.iin) problems.push(this.$t('chpo.personnel.errPasswordNoIin'));

        if (problems.length) {
          this.error = this.$t('chpo.personnel.errFillFields', { list: problems.join(', ') });
          return;
        }
      }

      try {
        if (row.__new) {
          // Создание нового
          const newUser = await createUser(payload);
          // Привязка к ЧПО
          if (this.chpoId) {
            await linkUserToChpo({ chpo_id: this.chpoId, user_id: newUser.id });
          }
          row.id = newUser.id;
          row.__new = false;
        } else {
          // Обновление существующего
          // Важно: используем user_id, а не id (который может быть chpo_user_id)
          const uid = row.user_id || row.id;
          await updateUser(uid, payload);
          // Сохраняем note отдельно в chpo/chpo-user/{id}/ как description
          if (row.id) {
            await updateChpoUser(row.id, { description: row.note || null });
          }
        }
        row.editing = false;
        delete row.__backup;
        // Обновляем список чтобы подтянулись правильные лейблы званий/должностей
        // Важно: сначала обновляем справочники (если вдруг что-то новое), потом строки
        // Инвалидируем контекст, чтобы подтянуть свежие данные с сервера
        refreshTodayChpoContext();
        await this.fetchRows(); 
      } catch (e) {
        if (row.__backup) { Object.assign(row, row.__backup); delete row.__backup; }
        // Не сбрасываем editing чтобы пользователь мог исправить
        console.error('Ошибка сохранения пользователя', e);
        const data = e?.response?.data;
        const details = (data && (data.detail || data.error)) ? (data.detail || data.error) : null;
        const fallback = details || (data ? JSON.stringify(data) : null) || e.message;
        this.error = this.$t('chpo.personnel.errSave', { msg: fallback });
      }
    },
    onDecline(row) {
      if (row.__new) {
        this.rows = this.rows.filter(r => r !== row);
        return;
      }
      if (row.__backup) {
        const b = row.__backup;
        Object.keys(b).forEach(k => { if (k !== '__backup') row[k] = b[k]; });
        delete row.__backup;
      }
      row.editing = false;
    },
    async onArchive(row) {
      try {
        // Используем chpo_user_id (row.id) для архивации связи
        await archiveChpoUser(row.id);
        refreshTodayChpoContext();
        await this.fetchRows();
      } catch (e) {
        console.error('Ошибка архивaции', e);
        this.error = this.$t('chpo.personnel.errArchive');
      }
    },
    async onRestore(row) {
      try {
        await restoreUser(row.id);
        await this.fetchRows();
      } catch (e) {
        console.error('Ошибка восстановления', e);
        this.error = this.$t('chpo.personnel.errRestore');
      }
    },
    openDetail(row) {
      // Модалка тянет /users/admin/{pk}/ по user_id (НЕ chpo_user_id).
      // row.id — это chpo_user_id; фолбэк на него дал бы 404.
      const pid = row.user_id
      if (!pid) return
      this.detail.personId = pid
      this.detail.open = true
    },
    async onDetailSaved() {
      try { await this.fetchRows() } catch (_) { /* silent */ }
    },
    addRow() {
      const nextNum = this.rows.length + 1;
      const tempId = Date.now();
      const newRow = {
        id: tempId,
        num: String(nextNum),
        rank: null, position: null, fio: '', birth: '', call: '', arrive: '', depart: '', note: '', order: '',
        // Расширенные поля по ТЗ блок 6.1
        iin: '', password: '', user_category: null, stpn_type: null,
        address: '', phone_number: '',
        is_active: true, is_stpn: false,
        editing: true,
        __new: true
      };
      this.rows = [...this.rows, newRow];
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-');
        return `${d}.${m}.${y}`;
      }
      return dateStr;
    },
    parseDate(dateStr) {
      if (!dateStr) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [d, m, y] = dateStr.split('.');
        return `${y}-${m}-${d}`;
      }
      return null;
    },
    formatMonthYear(dateStr) {
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-');
        return `${d}.${m}.${y}`;
      }
      return dateStr;
    },
    formatToDateInput(dateStr) {
      // Конвертирует ДД.ММ.ГГГГ -> ГГГГ-ММ-ДД
      if (!dateStr) return '';
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('.');
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    },
    formatFromDateInput(dateStr) {
      // Конвертирует ГГГГ-ММ-ДД -> ДД.ММ.ГГГГ
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
      }
      return dateStr;
    },
    parseMonthYear(dateStr) {
      if (!dateStr) return null;
      // YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      // DD.MM.YYYY
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [d, m, y] = dateStr.split('.');
        return `${y}-${m}-${d}`;
      }

      // MM.YYYY (month/year) -> YYYY-MM-01
      if (/^\d{2}\.\d{4}$/.test(dateStr)) {
        const [m, y] = dateStr.split('.');
        return `${y}-${m}-01`;
      }

      // YYYY-MM -> YYYY-MM-01
      if (/^\d{4}-\d{2}$/.test(dateStr)) {
        return `${dateStr}-01`;
      }

      return null;
    }
  }
};
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px 24px 120px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page__tabs { display: inline-flex; gap: 8px; }
.tabs__btn {
  border: 1px solid #E5E7EB;
  background: #fff;
  color: #111827;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
}
.tabs__btn.is-active { background: #2F78EE; color: #fff; border-color: #2F78EE; }

.page__title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 33.6px;
}

.page__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.page__head .page__title { flex: 1 1 auto; min-width: 0; }
.page__head-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

.page__search { width: 320px; max-width: 100%; padding: 8px 14px; border-radius: 10px; outline: 1px #E9EAEC solid; outline-offset: -1px; display: inline-flex; align-items: center; gap: 10px; background: #FFFFFF; box-sizing: border-box; flex: 0 0 auto; }
.page__search-input { flex: 1 1 0; border: none; outline: none; background: transparent; color: #111827; font-size: 14px; line-height: 22.4px; font-family: 'Times New Roman', Times, serif; }
.page__search-input::placeholder { color: #A0AEC0; }
.page__search-icon { width: 20px; height: 20px; background: no-repeat center/contain url('@/assets/images/search.svg'); display: inline-block; }

.page__table { width: 100%; }

.cell-text { flex: 1 1 0; color: #111827; font-size: 16px; font-family: 'Times New Roman', Times, serif; font-weight: 400; line-height: 20px; width: 100%; text-align: center; }
.tbl-input { width: 100%; height: 32px; padding: 6px 10px; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 16px; line-height: 20px; font-family: 'Times New Roman', Times, serif; color: #111827; background: #fff; }
.tbl-input:focus { outline: 2px solid rgba(47,120,238,.25); outline-offset: 0; }

/* Prevent date value truncation in narrow cells (Chrome). */
.tbl-input[type="date"] {
  min-width: 150px;
}

.actions { display: inline-flex; align-items: center; gap: 10px; justify-content: flex-end; width: 100%; }
.actions__btn { width: 30px; height: 30px; padding: 6px; border-radius: 8px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__btn--edit { background: #2F78EE; }
.actions__btn--accept { background: #2A8A1B; }
.actions__btn--decline { background: #D81717; }
.actions__btn--archive { background: #FFC600; }
.actions__btn--card { background: #6366F1; color: #fff; width: auto; padding: 6px 10px; font-size: 12px; font-weight: 600; }
.actions__label { color: #fff; font-family: Arial, Helvetica, sans-serif; line-height: 1; }

.page__pagination { display: flex; }

/* Расширенные поля (ТЗ «Рабочие задачи» блок 6.1) */
.extra-form {
  margin: 16px 0 24px;
  padding: 16px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  background: #F9FAFB;
}
.extra-form__title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}
.extra-form__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 16px;
}
.extra-form__field { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #374151; }
.extra-form__field span em { color: #DC2626; font-style: normal; }
.extra-form__checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 18px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}
.extra-form__hint { margin-top: 10px; font-size: 12px; color: #6B7280; }
.extra-form .tbl-input.is-invalid { border-color: #DC2626; }

/* Нижняя панель зафиксирована во всю ширину — при вертикальном скролле таблицы
   кнопки «Назад / Далее» остаются в поле зрения; фон белый на всю ширину. */
.page__footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  background: #ffffff;
  padding: 20px 32px;
  box-shadow: 0 -1px 0 rgba(15, 23, 42, 0.06);
}
.page__footer-nav { margin: 0; }
/* Внутри уже белой полосы фиксированного футера у пейджера не должно быть
   собственных отступов — стилистика самих кнопок задана в ViewNavButtons. */
.page__footer :deep(.pager) { margin: 0; }
</style>
